import { apiClient } from "@/lib/apiClient";

export interface NotificationTriggers {
  [key: string]: boolean;
}

export interface NotificationSettings {
  email?: NotificationTriggers;
  sms?: NotificationTriggers;
  whatsapp?: NotificationTriggers;
}

export interface UserPreferences {
  [key: string]: any;
}

export interface AdminUserSettings {
  language?: "en" | "es" | "fr" | "de";
  notifications?: NotificationSettings;
  preferences?: UserPreferences;
}

export interface UpdateAdminUserSettingsRequest {
  language?: "en" | "es" | "fr" | "de";
  notifications?: NotificationSettings;
  preferences?: UserPreferences;
}

export interface AdminUserSettingsResponse {
  message: string;
  valid: boolean;
  data: AdminUserSettings;
}

export interface PaymentSettings {
  prepaid?: boolean;
  semiprepaid?: boolean;
  postpaid?: boolean;
}

export interface PaymentSettingsResponse {
  message: string;
  valid: boolean;
  data: PaymentSettings;
}

export interface UpdatePaymentSettingsRequest {
  prepaid?: boolean;
  semiprepaid?: boolean;
  postpaid?: boolean;
}

export interface ShippingSettings {
  // Define based on your shipping settings structure
  [key: string]: any;
}

export interface ShippingSettingsResponse {
  message: string;
  valid: boolean;
  data: ShippingSettings;
}

export interface StoreSettings {
  // Define based on your store settings structure
  [key: string]: any;
}

export interface StoreSettingsResponse {
  message: string;
  valid: boolean;
  data: StoreSettings;
}

// Admin User Settings (merged notifications + user settings)
export async function getAdminUserSettings(
  storeId: string,
): Promise<AdminUserSettings> {
  try {
    const response = await apiClient.get<AdminUserSettingsResponse>(
      `/api/v1/admin/${storeId}/settings/admin-user`,
    );

    if (!response.valid) {
      throw new Error(
        response.message || "Failed to fetch admin user settings",
      );
    }

    return response.data.data;
  } catch (error) {
    console.error("Get admin user settings error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error(
      "Network error occurred while fetching admin user settings",
    );
  }
}

export async function updateAdminUserSettings(
  storeId: string,
  settings: UpdateAdminUserSettingsRequest,
): Promise<AdminUserSettings> {
  try {
    const response = await apiClient.put<AdminUserSettingsResponse>(
      `/api/v1/admin/${storeId}/settings/admin-user`,
      settings,
    );

    if (!response.valid) {
      throw new Error(
        response.message || "Failed to update admin user settings",
      );
    }

    return response.data.data;
  } catch (error) {
    console.error("Update admin user settings error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error(
      "Network error occurred while updating admin user settings",
    );
  }
}

// Payment Settings
export async function getPaymentSettings(
  storeId: string,
): Promise<PaymentSettings> {
  try {
    const response = await apiClient.get<PaymentSettingsResponse>(
      `/api/v1/admin/${storeId}/settings/payment`,
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to fetch payment settings");
    }

    return response.data.data;
  } catch (error) {
    console.error("Get payment settings error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while fetching payment settings");
  }
}

export async function updatePaymentSettings(
  storeId: string,
  settings: UpdatePaymentSettingsRequest,
): Promise<PaymentSettings> {
  try {
    const response = await apiClient.put<PaymentSettingsResponse>(
      `/api/v1/admin/${storeId}/settings/payment`,
      settings,
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to update payment settings");
    }

    return response.data.data;
  } catch (error) {
    console.error("Update payment settings error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while updating payment settings");
  }
}

// Shipping Settings
export async function getShippingSettings(
  storeId: string,
): Promise<ShippingSettings> {
  try {
    const response = await apiClient.get<ShippingSettingsResponse>(
      `/api/v1/admin/${storeId}/settings/shipping`,
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to fetch shipping settings");
    }

    return response.data.data;
  } catch (error) {
    console.error("Get shipping settings error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while fetching shipping settings");
  }
}

// Store Settings
export async function getStoreSettings(
  storeId: string,
): Promise<StoreSettings> {
  try {
    const response = await apiClient.get<StoreSettingsResponse>(
      `/api/v1/admin/${storeId}/settings/store`,
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to fetch store settings");
    }

    return response.data.data;
  } catch (error) {
    console.error("Get store settings error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while fetching store settings");
  }
}

export interface UpdateStoreSettingsRequest {
  store_name?: string;
  site_type?: string;
  google_analytics_code?: string;
  meta_pixel_code?: string;
}

export async function updateStoreSettings(
  storeId: string,
  settings: UpdateStoreSettingsRequest,
): Promise<StoreSettings> {
  try {
    const response = await apiClient.post<StoreSettingsResponse>(
      `/api/v1/admin/${storeId}/settings/store`,
      settings,
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to update store settings");
    }

    return response.data.data;
  } catch (error) {
    console.error("Update store settings error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while updating store settings");
  }
}
